import { useQuery } from '@apollo/client';
import { GET_CONTACT_AND_CONVERSATION_BY_ID } from '@zaplify/graphql';
import React, { FC, Suspense, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { MessageComposerProvider } from '../../../../hooks/messaging/use-message-composer';
import { MessageListSkeleton } from '../../../actions/components/message-list';
import { useProspectSync } from '../../../../hooks/use-prospect-sync';
import { useLinkedin } from '../../../../hooks/use-linkedin';
import { getProspectLinkedinTracker } from '../../../../sdk/internal/tracker.sdk';
import { useQuery as useTanQuery } from '@tanstack/react-query';
import { useIsMobile } from '@shadcn/ui/hooks/use-mobile';
import { ActionMessageList, ActionMessageListSkeleton } from './action-message-list';
import { ContactAvatar } from '../../../../components/contact-avatar';
import { Skeleton } from '@shadcn/ui/components/ui/skeleton';
import { SparklesAI } from '../../../actions/components/message-composer/sparkles-ai';
import { Loader2 } from 'lucide-react';
import { MessageComposer } from '../../../actions/components/message-composer/message-composer';
import { ActionConversationPageHeader } from './action-conversation-header';
import { ContactDetailsSidebar } from '../../../../components/contact-details-sidebar/contact-details-sidebar';
import { useSidebars } from '@shadcn/ui/hooks/use-sidebars';
import { MessageComposerProviderV2 } from '../../../../hooks/messaging/message-composer/use-message-composer-v2';
import { MessageComposerV2 } from '../../../actions/components/message-composer/message-composer-v2';

export const ActionConversation: FC = () => {
    const { contactId } = useParams();
    const scrollAreaRef = React.useRef<HTMLDivElement>(null);
    const { toggleSidebar, sidebarState } = useSidebars();

    // const { prospectIdsCurrentlySyncing } = useProspectSync();
    // const {
    //     prospect: { getProspectById },
    // } = useSdk();
    // const { data: prospectData } = useQuery(getProspectById(prospectId));
    const { linkedinAccount } = useLinkedin();
    // const { data: linkedinTracker } = useTanQuery(getProspectLinkedinTracker(contact.prospectId, linkedinAccount?.id));
    const [messageComposerSize, setMessageComposerSize] = React.useState<'large' | 'small'>('large');
    const [lastSizeToggleTimestamp, setLastSizeToggleTimestamp] = React.useState(0);
    const isMobile = useIsMobile();

    // const [isSyncingMessages, setIsSyncingMessages] = React.useState(prospectIdsCurrentlySyncing.has(prospectId));
    const [isSyncingMessages, setIsSyncingMessages] = React.useState(false);

    const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
        if (isMobile) {
            return;
        }
        if (Date.now() - lastSizeToggleTimestamp < 100) {
            return;
        }
        const element = e.target as HTMLDivElement;
        const isOverflowing = element.scrollHeight > element.clientHeight;
        const scrollBottom = element.scrollHeight - element.scrollTop - element.clientHeight;

        if (isOverflowing) {
            if (messageComposerSize === 'small' && scrollBottom <= 1) {
                setMessageComposerSize('large');
                scrollAreaRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
                setLastSizeToggleTimestamp(Date.now());
            } else if (messageComposerSize === 'large' && scrollBottom > 60) {
                setMessageComposerSize('small');
                setLastSizeToggleTimestamp(Date.now());
            }
        }
    };

    const { data, loading, error } = useQuery(GET_CONTACT_AND_CONVERSATION_BY_ID, {
        variables: { id: contactId, messageLimit: 20, noteLimit: 20 },
    });

    const contact = useMemo(() => {
        return data?.Contacts_by_pk;
    }, [data]);

    if (loading) {
        return null;
    }

    if (error) {
        return <div>Error: {error.message}</div>;
    }
    return (
        <div className="flex h-full w-full max-h-full overflow-hidden">
            <div className="flex flex-col h-full w-full">
                <div className="flex flex-col h-full w-full justify-end">
                    <ActionConversationPageHeader
                        contact={contact}
                        onToggleDetails={() => {
                            toggleSidebar('details');
                        }}
                        isDetailsOpen={sidebarState.details}
                    />
                    <Suspense fallback={<ActionMessageListSkeleton />}>
                        {contact?.Messages?.length > 0 ? (
                            <ActionMessageList
                                scrollAreaRef={scrollAreaRef}
                                onScroll={handleScroll}
                                messages={contact?.Messages}
                                contact={contact}
                            />
                        ) : (
                            <div className="flex flex-col h-full justify-between items-center">
                                <div className="flex flex-col h-full">
                                    <div className="flex flex-col items-center justify-center gap-3 p-10">
                                        <ContactAvatar
                                            avatarUrl={contact?.linkedinProfileImgUrl}
                                            fullName={contact?.fullName}
                                            size="xl"
                                        />
                                        {contact?.fullName ? (
                                            <span className="text-md font-medium">{contact.fullName}</span>
                                        ) : (
                                            <Skeleton className="h-6 w-48 bg-background-brand-primary-subtle" />
                                        )}
                                        <div className="flex flex-row bg-background-tertiary rounded-xl p-2 items-center justify-center text-sm text-center">
                                            <span>
                                                Adjust the message as needed using the
                                                <div className=" mx-1 inline-block">
                                                    <SparklesAI height={'14px'} />
                                                </div>
                                                button and get the conversation going.
                                            </span>
                                        </div>
                                    </div>
                                </div>
                                {isSyncingMessages && (
                                    // Syncing messages loader box
                                    <div className="flex flex-row justify-center items-center w-fit p-4 gap-2 mb-8 border rounded-xl">
                                        Syncing messages...
                                        <Loader2 className="h-6 w-6 animate-spin" />
                                    </div>
                                )}
                            </div>
                        )}
                    </Suspense>

                    <div className="flex bottom-auto left-auto right-auto mb-14 md:mb-0 md:px-4 md:pb-4 md:w-full md:min-w-full bg-background flex-shrink-0">
                        <MessageComposerProviderV2 key={contactId} contactId={contact?.id}>
                            <Suspense
                                fallback={
                                    <Skeleton className="h-[200px] w-full border-4 border-background-brand-primary-subtle">
                                        <Skeleton className="m-5 w-[80%] h-2 bg-background-brand-primary-subtle" />
                                    </Skeleton>
                                }
                            >
                                <MessageComposerV2 size={messageComposerSize} intercomAdjustedSendButton />
                            </Suspense>
                        </MessageComposerProviderV2>
                    </div>
                </div>
            </div>
            <ContactDetailsSidebar />
        </div>
    );
};
